import { useNavigate } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import NativeCheckoutDialog from "src/components/blocks/dialogs/NativeCheckoutDialog.js";
import { LoadingButton } from "../../blocks/LoadingButton.js";
import {
  Typography,
  Box,
  Icon,
  Switch,
  FormControlLabel,
  Button,
} from "@mui/material";
import BottomDialog from "../../blocks/dialogs/BottomDialog.js";
import CenterDialog from "../../blocks/dialogs/CenterDialog.js";
import { getPageRoute } from "src/services";
import useGetCalendarFromParams from "src/utils/useGetCalendarFromParams.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { useTheme } from "@emotion/react";
import { useDimensions } from "src/utils/useDimensions.js";
import OpenCalendarDialog from "../../blocks/dialogs/OpenCalendarDialog.js";
import { useSubscribe } from "src/utils/useSubscribe.js";
import { customEvent } from "src/utils/gtag.js";

const CalendarStepsPersonalization = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { calendar, isLoadingCalendar, isCalendarError } =
    useGetCalendarFromParams();
  const theme = useTheme();
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const { subscribe } = useSubscribe();
  const { isMediumScreen, isExtraSmallScreen, isSmallScreen } = useDimensions();
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openUpgradeDialog, setOpenUpgradeDialog] = useState(false);

  // Toggle states for the 4 features
  const [realtimeUpdates, setRealtimeUpdates] = useState(false);
  const [verifiedUpdates, setVerifiedUpdates] = useState(false);
  const [turnOffAds, setTurnOffAds] = useState(false);
  const [autoSync, setAutoSync] = useState(false);

  useEffect(() => {
    customEvent({
      name: "landed_on_calendar_personalization",
      category: "CalendarPersonalization",
      label: "Landed on Calendar Personalization",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });
  }, []);

  // Check if any premium features are enabled
  const hasAnyPremiumFeatures =
    realtimeUpdates || verifiedUpdates || turnOffAds || autoSync;

  const handleContinue = () => {
    if (hasAnyPremiumFeatures) {
      // Skip dialog and go directly to payment
      customEvent({
        name: "user_click_try_7_days_free",
        category: "CalendarPersonalization",
        label: "Try 7 Days Free Button Clicked (Direct)",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          calendar_handle: calendar?.handle,
          selected_features: {
            realtime_updates: realtimeUpdates,
            auto_sync: autoSync,
            verified_updates: verifiedUpdates,
            turn_off_ads: turnOffAds,
          },
        },
      });
      setOpenPaymentDialog(true);
    } else {
      // Continue with basic version - open copy link dialog
      customEvent({
        name: "user_click_continue_no_personalization",
        category: "CalendarPersonalization",
        label: "Continue Button Clicked",
        data: {
          user_id: user?._id,
          user_email: user?.email,
        },
      });
      subscribe({ calendar });
      setOpenCalendarDialog(true);
    }
  };

  const handleTryFreeTrial = () => {
    customEvent({
      name: "user_click_try_7_days_free",
      category: "CalendarPersonalization",
      label: "Try 7 Days Free Button Clicked",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
        selected_features: {
          realtime_updates: realtimeUpdates,
          auto_sync: autoSync,
          verified_updates: verifiedUpdates,
          turn_off_ads: turnOffAds,
        },
      },
    });
    setOpenUpgradeDialog(false);
    setOpenPaymentDialog(true);
  };

  const handleContinueBasic = () => {
    customEvent({
      name: "user_click_continue_basic",
      category: "CalendarPersonalization",
      label: "Continue with Basic Version Button Clicked",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
        selected_features: {
          realtime_updates: realtimeUpdates,
          auto_sync: autoSync,
          verified_updates: verifiedUpdates,
          turn_off_ads: turnOffAds,
        },
      },
    });
    setOpenUpgradeDialog(false);
    subscribe({ calendar });
    setOpenCalendarDialog(true);
  };

  const UpgradeDialogContent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        padding: 2,
      }}
    >
      <Typography
        variant="title"
        sx={{ fontSize: "26px", textWrap: "pretty", mb: 3, mt: 1 }}
      >
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            color: `${theme.palette.icon.primary}`,
            fontSize: "24px",
            verticalAlign: "middle",
            mr: 0.5,
            mb: 0.5,
          }}
        >
          verified_user
        </Icon>
        Stanza+ is built for you
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.5 }}>
        You care about staying up to date and keeping your calendar clean.
        <br></br>
        <br></br>Try Stanza+ free for 7 days to make sure you're getting the
        most accurate, ad-free version. You can switch back anytime.
      </Typography>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleTryFreeTrial}
        sx={{
          fontWeight: 600,
          mb: 2,
        }}
      >
        Try 7 Days Free
      </Button>
      <Button
        variant="text"
        color="primary"
        fullWidth
        onClick={handleContinueBasic}
        sx={{
          fontWeight: 500,
          textTransform: "none",
          color: "text.secondary",
        }}
      >
        CONTINUE WITH DEFAULT VERSION
      </Button>
    </Box>
  );

  const FeatureToggle = ({
    checked,
    onChange,
    title,
    subcopy,
    icon,
    isLast = false,
    trackingName,
  }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        py: 2.5,
        borderBottom: isLast ? "none" : `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", flex: 1, pr: 2, alignItems: "flex-start" }}>
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            color: `${theme.palette.icon.primary}`,
            fontSize: "1.4rem",
            mr: 1.5,
            mt: 0.1,
          }}
        >
          {icon}
        </Icon>
        <Box>
          <Typography
            variant="body1"
            sx={{ fontWeight: 500, mb: 0.5, textAlign: "left" }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "left" }}
          >
            {subcopy}
          </Typography>
        </Box>
      </Box>
      <Switch
        checked={checked}
        onChange={(e) => {
          onChange(e);
          customEvent({
            name: `toggle_${trackingName}`,
            category: "CalendarPersonalization",
            label: `Toggle ${title}`,
            data: {
              user_id: user?._id,
              user_email: user?.email,
              calendar_handle: calendar?.handle,
              toggle_state: e.target.checked ? "on" : "off",
              feature: trackingName,
            },
          });
        }}
        color="primary"
      />
    </Box>
  );

  return (
    <InstructionsLayout nav="back">
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="h1"
          sx={{
            mr: "auto",
            textAlign: "left",
            fontSize: "2rem !important",
            lineHeight: "1.4 !important",
            fontWeight: "500 !important",
          }}
        >
          Customize your Subscription
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 4,
          mt: 2,
          p: 2.5,
          borderRadius: "12px",
          background: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow:
            "0 8px 32px rgba(0, 0, 0, 0.1), 0 1px 0 rgba(255, 255, 255, 0.5) inset",
        }}
      >
        <FeatureToggle
          checked={turnOffAds}
          onChange={(e) => setTurnOffAds(e.target.checked)}
          title="Turn off ads"
          subcopy="Ads are turned on by default."
          icon="block"
          trackingName="turn_off_ads"
        />

        <FeatureToggle
          checked={realtimeUpdates}
          onChange={(e) => setRealtimeUpdates(e.target.checked)}
          title="Near real-time updates"
          subcopy="Default calendars update every 1–2 days."
          icon="sync"
          trackingName="realtime_updates"
        />

        <FeatureToggle
          checked={autoSync}
          onChange={(e) => setAutoSync(e.target.checked)}
          title="Automatic sync across all devices"
          subcopy="Default requires manual re-subscribe per device."
          icon="devices"
          trackingName="auto_sync"
        />

        <FeatureToggle
          checked={verifiedUpdates}
          onChange={(e) => setVerifiedUpdates(e.target.checked)}
          title="Know what changed, when"
          subcopy="Calendars update silently by default. You won't know what changed and if you're seeing the latest."
          icon="verified"
          trackingName="verified_updates"
          isLast={true}
        />
      </Box>

      {/* Desktop button */}
      <LoadingButton
        size="grown"
        variant="contained"
        color="primary"
        sx={{
          mt: 2,
          height: "auto",
          width: "100%",
          display: { xs: "none", sm: "flex" }, // Hide on mobile, show on desktop
        }}
        onClick={handleContinue}
      >
        Continue
      </LoadingButton>

      {/* Fixed bottom button for mobile only */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "20px",
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          display: { xs: "flex", sm: "none" }, // Show on mobile, hide on desktop
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <LoadingButton
          size="grown"
          variant="contained"
          color="primary"
          sx={{ width: "100%", maxWidth: "400px" }}
          onClick={handleContinue}
        >
          Continue
        </LoadingButton>
      </Box>

      {/* Upgrade Dialog - Responsive */}
      {isSmallScreen ? (
        <BottomDialog
          open={openUpgradeDialog}
          handleClose={() => setOpenUpgradeDialog(false)}
          hideCloseButton
          hideRectangle
          bubbleStyle
        >
          <UpgradeDialogContent />
        </BottomDialog>
      ) : (
        <CenterDialog
          open={openUpgradeDialog}
          handleClose={() => setOpenUpgradeDialog(false)}
          sx={{ height: "auto", ".MuiPaper-root": { maxWidth: "400px" } }}
          hideCloseButton
        >
          <UpgradeDialogContent />
        </CenterDialog>
      )}

      {/* Copy Link Dialog */}
      <OpenCalendarDialog
        open={openCalendarDialog}
        handleClose={() => setOpenCalendarDialog(false)}
        calendar={calendar}
      />

      {/* Native Checkout Dialog */}
      <NativeCheckoutDialog
        open={!!openPaymentDialog}
        successUrl={getPageRoute("help", "CALENDAR_CONNECTED", {
          handle: calendar?.handle,
          calendarId: calendar?._id,
        })}
        calendar={calendar}
        onClose={() => {
          setOpenPaymentDialog(false);
        }}
      />
    </InstructionsLayout>
  );
};

export default CalendarStepsPersonalization;
